import { useRef, useEffect, useCallback, useState } from 'react';
import { useEditorStore } from '../store/editorStore';
import { usePlayheadStore } from '../store/playheadStore';
import { useTimelineInteraction } from '../hooks/useTimelineInteraction';
import { generateThumbnails, getThumbnails } from '../engine/thumbnailGenerator';
import { getWaveform, getAudioBufferData } from '../engine/proxyManager';
import { generatePeaksAtZoom, type PeakData } from '../engine/peakGenerator';
import {
  drawRuler,
  drawTrackLanes,
  drawClipBlocks,
  drawThumbnails,
  drawWaveform,
  drawPlayhead,
  drawTrimHandles,
  drawFadeOverlay,
  drawSelectionRegion,
  drawEffectClips,
  setDrawingProject,
  getTotalTracksHeight,
  type DrawContext,
} from '../lib/canvasDrawing';
import { TimelineContextMenu, type ContextMenuState } from './TimelineContextMenu';
import { useAutoScroll } from '../hooks/useAutoScroll';
import { pixelToMs } from '../lib/timeUtils';
import { useLoopStore } from '../store/loopStore';

export function TimelineCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef(0);
  const { onMouseDown, onMouseMove, onMouseUp, onContextMenu, onDoubleClick, cursor } = useTimelineInteraction(canvasRef);
  const { onManualScroll } = useAutoScroll(canvasRef);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const setScrollOffset = useEditorStore((s) => s.setScrollOffset);

  // Loop region
  const loopStart = useLoopStore((s) => s.selectionStart);
  const loopEnd = useLoopStore((s) => s.selectionEnd);
  const loopEnabled = useLoopStore((s) => s.loopEnabled);

  const project = useEditorStore((s) => s.project);
  const tracks = project.tracks;
  const durationMs = project.durationMs;
  const timeline = useEditorStore((s) => s.timeline);

  // Collect unique assetIds from clips
  const assetIdKey = tracks.flatMap((t) => t.clips.map((c) => c.assetId)).join(',');

  // Maps loaded per-asset data (thumbnails + waveforms), keyed by assetId
  const [thumbsMap, setThumbsMap] = useState<Map<string, ImageBitmap[]>>(new Map());
  const [peaksMap, setPeaksMap] = useState<Map<string, PeakData | Float32Array>>(new Map());
  const audioBufferCache = useRef<Map<string, AudioBuffer>>(new Map());
  const zoomDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastZoomPxPerMs = useRef(timeline.pxPerMs);

  // Load thumbnails & waveforms for all assets used by clips
  useEffect(() => {
    const assetIds = new Set<string>();
    for (const track of tracks) {
      for (const clip of track.clips) {
        assetIds.add(clip.assetId);
      }
    }

    let cancelled = false;

    for (const assetId of assetIds) {
      // Thumbnails
      const cached = getThumbnails(assetId);
      if (cached) {
        setThumbsMap((prev) => {
          if (prev.get(assetId) === cached) return prev;
          const next = new Map(prev);
          next.set(assetId, cached);
          return next;
        });
      } else {
        generateThumbnails(assetId).then((result) => {
          if (cancelled) return;
          setThumbsMap((prev) => {
            const next = new Map(prev);
            next.set(assetId, result);
            return next;
          });
        });
      }

      // Waveforms (handles both PeakData and legacy Float32Array)
      getWaveform(assetId).then((data) => {
        if (cancelled || !data) return;
        setPeaksMap((prev) => {
          if (prev.get(assetId) === data) return prev;
          const next = new Map(prev);
          next.set(assetId, data);
          return next;
        });
      });

      // Pre-load AudioBuffer for zoom re-computation
      if (!audioBufferCache.current.has(assetId)) {
        getAudioBufferData(assetId).then(async (raw) => {
          if (cancelled || !raw) return;
          const ctx = new AudioContext();
          const buf = await ctx.decodeAudioData(raw.slice(0));
          await ctx.close();
          audioBufferCache.current.set(assetId, buf);
        });
      }
    }

    return () => { cancelled = true; };
  }, [assetIdKey, tracks]);

  // Re-generate peaks on zoom change (debounced 200ms)
  useEffect(() => {
    if (timeline.pxPerMs === lastZoomPxPerMs.current) return;
    lastZoomPxPerMs.current = timeline.pxPerMs;

    if (zoomDebounceRef.current) clearTimeout(zoomDebounceRef.current);
    zoomDebounceRef.current = setTimeout(() => {
      const buffers = audioBufferCache.current;
      if (buffers.size === 0) return;
      const newPeaks = new Map(peaksMap);
      let changed = false;
      for (const [assetId, buf] of buffers) {
        const pd = generatePeaksAtZoom(buf, timeline.pxPerMs);
        newPeaks.set(assetId, pd);
        changed = true;
      }
      if (changed) setPeaksMap(newPeaks);
    }, 200);

    return () => {
      if (zoomDebounceRef.current) clearTimeout(zoomDebounceRef.current);
    };
  }, [timeline.pxPerMs, peaksMap]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    setDrawingProject(project);

    const dc: DrawContext = {
      ctx,
      width: rect.width,
      height: rect.height,
      pxPerMs: timeline.pxPerMs,
      scrollOffsetMs: timeline.scrollOffsetMs,
      durationMs,
    };

    ctx.clearRect(0, 0, rect.width, rect.height);

    drawRuler(dc);
    drawTrackLanes(dc, tracks);
    drawClipBlocks(dc, tracks, timeline.selectedClipId);

    // Draw thumbnails and waveforms for ALL clips
    tracks.forEach((track, trackIndex) => {
      if (track.type === 'effect') return;
      for (const clip of track.clips) {
        if (track.type === 'video') {
          const thumbs = thumbsMap.get(clip.assetId);
          if (thumbs) {
            drawThumbnails(dc, thumbs, clip, trackIndex, 500, tracks);
          }
        } else {
          const peaks = peaksMap.get(clip.assetId);
          if (peaks) {
            drawWaveform(dc, peaks, clip, trackIndex, tracks);
          }
        }
      }
    });

    // Draw fade overlays (Phase 4)
    tracks.forEach((track, trackIndex) => {
      if (track.type === 'effect') return;
      for (const clip of track.clips) {
        if (clip.fadeIn || clip.fadeOut) {
          drawFadeOverlay(dc, clip, trackIndex, tracks);
        }
      }
    });

    // Draw effect clips
    const currentTimeMs = usePlayheadStore.getState().currentTimeMs;
    drawEffectClips(dc, tracks, timeline.selectedClipId, currentTimeMs);

    // Draw trim handles on selected clip
    drawTrimHandles(dc, tracks, timeline.selectedClipId);

    // Draw loop selection region (Phase 9)
    if (loopStart !== null && loopEnd !== null) {
      drawSelectionRegion(dc, loopStart, loopEnd, loopEnabled);
    }

    drawPlayhead(dc, currentTimeMs);
  }, [project, tracks, durationMs, timeline, thumbsMap, peaksMap, loopStart, loopEnd, loopEnabled]);

  useEffect(() => {
    function tick() {
      draw();
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [draw]);

  // Cmd/Ctrl+scroll = zoom, horizontal scroll = timeline pan, vertical scroll = native (track scrolling)
  const handleWheel = useCallback((e: WheelEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (e.metaKey || e.ctrlKey) {
      // Zoom centered on cursor — prevent default to stop page zoom
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const cursorX = e.clientX - rect.left;
      const { pxPerMs, scrollOffsetMs } = useEditorStore.getState().timeline;
      const cursorTimeMs = pixelToMs(cursorX, pxPerMs, scrollOffsetMs);

      // Smooth zoom: dampen deltaY for gradual pinch control
      const zoomSensitivity = 0.005;
      const scaleFactor = Math.exp(-e.deltaY * zoomSensitivity);
      const minPx = 0.005;
      const maxPx = 1.5;
      const newPxPerMs = Math.min(maxPx, Math.max(minPx, pxPerMs * scaleFactor));
      useEditorStore.getState().setZoom(newPxPerMs);

      const actualNewPxPerMs = useEditorStore.getState().timeline.pxPerMs;
      const newScrollOffset = cursorTimeMs - cursorX / actualNewPxPerMs;
      setScrollOffset(Math.max(0, newScrollOffset));
    } else if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      // Predominantly horizontal scroll → timeline horizontal pan
      e.preventDefault();
      onManualScroll();
      const { pxPerMs, scrollOffsetMs } = useEditorStore.getState().timeline;
      const deltaMs = e.deltaX / pxPerMs;
      setScrollOffset(Math.max(0, scrollOffsetMs + deltaMs));
    }
    // Vertical scroll: don't preventDefault — let it bubble to the scrollable parent
  }, [setScrollOffset, onManualScroll]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const handleContextMenu = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const result = onContextMenu(e);
    setContextMenu(result);
  }, [onContextMenu]);

  const totalHeight = getTotalTracksHeight(tracks);

  return (
    <div className="relative w-full" style={{ minHeight: totalHeight }}>
      <canvas
        ref={canvasRef}
        className="w-full"
        style={{ display: 'block', cursor, height: totalHeight }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onDoubleClick={onDoubleClick}
        onContextMenu={handleContextMenu}
      />
      {contextMenu && (
        <TimelineContextMenu
          menu={contextMenu}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
}
